import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery } from '@confluence/query-preloader-tools/entry-points/getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery.ts';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';

import { EndOfPageRecommendationQuery } from '../EndOfPageRecommendationQuery.graphql.ts';
import {
	type EndOfPageRecommendationQuery as EndOfPageRecommendationData,
	type EndOfPageRecommendationQueryVariables,
} from '../__types__/EndOfPageRecommendationQuery.ts';
import { transformAndOrderRecs } from '../transformers.ts';

export const usePageRecommendationData = (skip: boolean = false) => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();

	const { error, loading, data } = useQueryInterop<
		EndOfPageRecommendationData,
		EndOfPageRecommendationQueryVariables
	>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		...getQueryParamsAndSetFetchPolicyForNetworkOnlyQuery({
			query: EndOfPageRecommendationQuery,
			variables: {
				type: 'page',
				id: contentId ?? '',
				exp: 'end_of_page_v5',
				key: spaceKey ?? '',
				keyString: spaceKey ?? '',
			},
			context: { single: true },
			errorPolicy: 'all',
			skip,
		}),
	);

	const pageCardData = useMemo(
		() => transformAndOrderRecs(data?.getRecommendedPages?.recommendedPages),
		[data],
	);

	if (error || skip) {
		return {
			loading: false,
			error,
			data: undefined,
			hasRecs: false,
		};
	}

	return {
		loading,
		error,
		data: pageCardData,
		hasRecs: pageCardData && pageCardData.length > 2,
	};
};
